import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { BehaviorSubject, Observable, catchError, from, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserDataResponse, UserDetails } from '../interfaces/user.interface';
import { jwtDecode, JwtPayload } from "jwt-decode";

declare global {
  interface Window {
    dtrum?: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  userDetailsSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private http: HttpClient, private authService: MsalService) {
  }

  getUsers(): Observable<UserDataResponse> {
    return this.http.post<UserDataResponse>(`${this.apiUrl}/api/v1/getAuthUserData`, {}).pipe(tap(response => {
      this.userDetailsSubject$.next(response); // Cache the response
      this.identifyDynatraceUser(response?.responsedata);
    }),

    );
  };

  setToken(): void {
    this.authService.acquireTokenSilent({
      scopes: [...environment.apiConfig.scopes],
    }).subscribe({
      next: (response) => {
        console.log("token", response);
        localStorage.setItem("access_token", response.accessToken);
      },
      error: (error) => {
        console.error('Error acquiring token:', error);
        if (error instanceof InteractionRequiredAuthError) {
          this.authService.loginRedirect({
            scopes: [...environment.apiConfig.scopes]
          });
        }
      }
    });
  };

  refreshToken(): Observable<void> {
    return from(this.authService.acquireTokenSilent({
      scopes: [...environment.apiConfig.scopes]
    })).pipe(
      switchMap(response => {
        localStorage.setItem("access_token", response.accessToken);
        return new Observable<void>(observer => {
          observer.next();
          observer.complete();
        });
      }),
      catchError(error => {
        if (error instanceof InteractionRequiredAuthError) {
          this.authService.loginRedirect({
            scopes: [...environment.apiConfig.scopes]
          });
        }
        return throwError(error);
      })
    );
  }

  // Tags the Dynatrace session with the logged-in user so sessions are not reported as Anonymous.
  // dtrum is only present when the agent script was injected and the session was sampled.
  // Session property keys must match the properties defined on the Dynatrace application.
  private identifyDynatraceUser(user: UserDetails | undefined): void {
    const dtrum = window.dtrum;
    if (!user || !dtrum) return;
    try {
      const userId = user.email || user.az_uuid;
      if (userId) {
        dtrum.identifyUser(userId);
      }
      const properties: Record<string, string> = {};
      const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
      if (fullName) properties['fullname'] = fullName;
      if (user.usertype_lkp) properties['usertype'] = user.usertype_lkp;
      if (user.belongsto_org) properties['userorg'] = user.belongsto_org;
      if (Object.keys(properties).length) {
        dtrum.sendSessionProperties(null, null, properties);
      }
    } catch (error) {
      console.error('Dynatrace user identification failed:', error);
    }
  }

  logout() {
    // End the Dynatrace session so the next login in this browser is not attributed to this user
    try {
      window.dtrum?.endSession();
    } catch (error) {
      console.error('Dynatrace endSession failed:', error);
    }
    localStorage.clear();
    this.authService.logoutRedirect();
  }

  isTokenExpired(token: string | null): boolean {
    if (!token) return true; // No token, so consider expired
    // by default jwtDecode returns only payload part of the token, if we want header we need to pass second argument as { header: true }
    let decodedTokenPayload: JwtPayload;
    try{
      decodedTokenPayload = jwtDecode(token);
    }
    catch(e){
      console.error("Error decoding token");
      return true;
    }

    const tokenExpiration = decodedTokenPayload.exp;
    if (!tokenExpiration) return true; // No expiration info, consider expired
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return currentTimestamp >= tokenExpiration;
  }
}